import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import {
  Building2, GraduationCap, Briefcase, Target, Sparkles, BadgeCheck, BookOpen,
  Mail, ShieldCheck, Users, Eye, FileText, ArrowUpRight, ArrowRight, Layers,
  Cpu, Brain, Terminal, MonitorPlay, FlaskConical
} from "lucide-react";

const CORPORATE_IMG = "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1400&q=80";
const STUDENT_IMG = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80";

// Seven ecosystem pillars
const PILLARS = [
  {
    id: "corporate",
    icon: Building2,
    tag: "Pillar 01",
    audience: "Enterprises & Institutions",
    accent: "from-emerald-500 to-teal-500",
    title: "Corporate Security Training",
    desc: "Continuous, business-aligned training for enterprise teams — turning security culture into a measurable program.",
    items: [
      { icon: Users, label: "Employee Awareness", note: "Role-relevant micro-learning across the year." },
      { icon: Mail, label: "Phishing Simulation", note: "Continuous campaigns with culture dashboards." },
      { icon: ShieldCheck, label: "Secure Workplace Programs", note: "Onboarding, off-boarding, contractor hygiene." },
      { icon: Target, label: "Leadership Cyber Awareness", note: "Boardroom briefings and tabletop exercises." },
      { icon: BadgeCheck, label: "Compliance Workshops", note: "DPDP, ISO 27001 and SOC 2 enablement." },
    ],
  },
  {
    id: "student",
    icon: GraduationCap,
    tag: "Pillar 02",
    audience: "Students & Aspiring Professionals",
    accent: "from-[#3B82F6] to-[#6D28D9]",
    title: "Student Cyber Training",
    desc: "Hands-on cybersecurity learning for students — fundamentals through to live SOC exposure.",
    items: [
      { icon: BookOpen, label: "Cybersecurity Fundamentals", note: "Concepts, terminology and the modern threat landscape." },
      { icon: Eye, label: "Digital Safety Education", note: "Online safety, scams and reporting habits." },
      { icon: Terminal, label: "Ethical Hacking Basics", note: "Recon, vulnerabilities and responsible disclosure." },
      { icon: MonitorPlay, label: "SOC Exposure", note: "Walkthroughs of real SOC dashboards and alerting." },
      { icon: FlaskConical, label: "Practical Learning Labs", note: "Sandboxed labs with safe, real-world scenarios." },
    ],
  },
  {
    id: "internship",
    icon: Briefcase,
    tag: "Pillar 03",
    audience: "Students Transitioning to Industry",
    accent: "from-amber-500 to-orange-500",
    title: "Internship Programs",
    desc: "Structured cybersecurity internships that put interns inside real customer engagements.",
    items: [
      { icon: ShieldCheck, label: "Cyber Internships", note: "3, 6 and 12-month structured programs." },
      { icon: Cpu, label: "SOC Analyst Exposure", note: "Tier-1 shadowing and triage drills." },
      { icon: Layers, label: "Live Project Learning", note: "Embedded in customer deployments under mentorship." },
      { icon: Briefcase, label: "Workforce Readiness", note: "Reporting, customer interaction and on-the-job rotations." },
    ],
  },
  {
    id: "industry-readiness",
    icon: Target,
    tag: "Pillar 04",
    audience: "Final-year & Early-career Talent",
    accent: "from-rose-500 to-pink-500",
    title: "Industry Readiness Programs",
    desc: "Bridging the gap between certifications and a productive first 90 days in a cyber role.",
    items: [
      { icon: Users, label: "Interview Preparation", note: "Mock interviews, technical and behavioural rounds." },
      { icon: Brain, label: "Cybersecurity Career Guidance", note: "Role mapping — SOC, GRC, engineering, awareness." },
      { icon: Terminal, label: "Technical Readiness", note: "Hands-on toolchain and incident-handling drills." },
      { icon: Building2, label: "Enterprise Workflow Understanding", note: "How real security teams operate end-to-end." },
    ],
  },
  {
    id: "skill-india",
    icon: Sparkles,
    tag: "Pillar 05",
    audience: "National Skilling Alignment",
    accent: "from-cyan-500 to-blue-500",
    title: "Skill India Initiatives",
    desc: "Cybersecurity skilling programs aligned to national workforce development priorities.",
    items: [
      { icon: GraduationCap, label: "Workforce Development", note: "Skill India aligned cyber curriculum tracks." },
      { icon: ShieldCheck, label: "Cybersecurity Upskilling", note: "Continuing education for working professionals." },
      { icon: Layers, label: "Digital India Alignment", note: "Programs that map to public-sector skilling goals." },
    ],
  },
  {
    id: "certification",
    icon: BadgeCheck,
    tag: "Pillar 06",
    audience: "Certification Pathways",
    accent: "from-violet-500 to-fuchsia-500",
    title: "Certification Preparation",
    desc: "Structured preparation paths for the certifications hiring managers actually look for.",
    items: [
      { icon: FileText, label: "Cybersecurity Certifications", note: "Preparation tracks for industry-recognised certifications." },
      { icon: BookOpen, label: "Security Fundamentals", note: "Foundational concepts before specialisation." },
      { icon: Cpu, label: "Enterprise Security Learning Paths", note: "Endpoint, identity, cloud, GRC tracks." },
    ],
  },
  {
    id: "awareness",
    icon: Eye,
    tag: "Pillar 07",
    audience: "Schools · Colleges · Communities",
    accent: "from-slate-500 to-slate-700",
    title: "Awareness Workshops",
    desc: "On-ground digital safety workshops — bringing cyber awareness to schools, colleges and community groups.",
    items: [
      { icon: GraduationCap, label: "School Workshops", note: "Online safety, social media hygiene, gaming risks." },
      { icon: BookOpen, label: "College Workshops", note: "Practical safety, careers in cyber and ethical hacking." },
      { icon: Users, label: "Community Workshops", note: "RWAs, senior citizens, merchants and women's groups." },
      { icon: Sparkles, label: "Awareness Campaigns", note: "City and regional-level digital safety drives." },
    ],
  },
];

// Two-pillar journeys for the architecture section
const CORPORATE_JOURNEY = [
  { step: "01", label: "Risk Assessment", note: "Identify weakest links: people, processes, identity." },
  { step: "02", label: "Program Design", note: "Role-relevant curriculum, frequency and channels." },
  { step: "03", label: "Continuous Delivery", note: "Micro-learning, simulations, leadership briefings." },
  { step: "04", label: "Measure & Improve", note: "Click-rate, engagement and culture dashboards." },
];

const STUDENT_JOURNEY = [
  { step: "01", label: "Foundations", note: "Fundamentals · digital safety · ethical hacking basics." },
  { step: "02", label: "Hands-on Labs", note: "SOC exposure · practical scenarios · safe environments." },
  { step: "03", label: "Internship", note: "Live project embedding with mentorship." },
  { step: "04", label: "Industry Readiness", note: "Interview prep · career guidance · placement support." },
];

export default function Training() {
  return (
    <div data-testid="training-page">
      <PageHero
        eyebrow="Cyber Training & Workforce Development Ecosystem"
        title="A training ecosystem — for enterprises and the next generation"
        description="Cosmos Defence runs an India-focused cybersecurity training ecosystem that serves both enterprises building security culture and students preparing for real cyber careers — operationally linked, mission-driven, workforce-oriented."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact?type=consultation&service=training" className="btn-accent" data-testid="training-hero-cta">
            Design a Training Program <ArrowUpRight size={16} />
          </Link>
          <Link to="/workforce" className="btn-ghost" data-testid="training-workforce-cta">
            Workforce Ecosystem <ArrowRight size={14} />
          </Link>
        </div>
      </PageHero>

      {/* Two pillars architecture */}
      <section data-testid="training-pillars-section" className="py-20 bg-white">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Two-Pillar Architecture"
            title="Corporate culture · Student talent · One ecosystem"
            description="The same instructors, labs, and SOC operators that train enterprise teams also train the next generation of cyber professionals — feeding a single workforce pipeline."
          />

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="training-pillars-grid">
            {/* Corporate */}
            <div className="card-cd !p-0 overflow-hidden" data-testid="training-pillar-corporate">
              <div className="relative h-56">
                <img src={CORPORATE_IMG} alt="Corporate cybersecurity training" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1B5E]/85 via-[#0A1B5E]/30 to-transparent" />
                <div className="absolute bottom-5 left-6">
                  <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-cyan-300">Corporate Track</div>
                  <h3 className="mt-1 font-display text-2xl font-bold text-white">Enterprise Security Training</h3>
                </div>
              </div>
              <div className="p-8">
                <p className="text-slate-600 leading-relaxed">Continuous awareness, phishing simulation, leadership and compliance training designed for enterprises that want security culture to be measurable.</p>
                <ol className="mt-7 space-y-4">
                  {CORPORATE_JOURNEY.map((j) => (
                    <li key={j.step} className="flex items-start gap-4">
                      <span className="font-mono-tech text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">{j.step}</span>
                      <div>
                        <div className="font-display font-bold text-[#0A1B5E]">{j.label}</div>
                        <div className="text-sm text-slate-600 mt-0.5">{j.note}</div>
                      </div>
                    </li>
                  ))}
                </ol>
                <Link to="/contact?type=consultation&service=training" className="btn-primary mt-7 text-sm" data-testid="training-corporate-cta">
                  Build a Corporate Program <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Student */}
            <div className="card-cd !p-0 overflow-hidden ring-glow-purple border-purple-200" data-testid="training-pillar-student">
              <div className="relative h-56">
                <img src={STUDENT_IMG} alt="Cybersecurity student training" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#6D28D9]/85 via-[#6D28D9]/30 to-transparent" />
                <div className="absolute bottom-5 left-6">
                  <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-cyan-300">Student Track</div>
                  <h3 className="mt-1 font-display text-2xl font-bold text-white">Cyber Workforce Readiness</h3>
                </div>
              </div>
              <div className="p-8">
                <p className="text-slate-600 leading-relaxed">A structured pathway from cybersecurity fundamentals to internships and a productive first 90 days in a real cyber role.</p>
                <ol className="mt-7 space-y-4">
                  {STUDENT_JOURNEY.map((j) => (
                    <li key={j.step} className="flex items-start gap-4">
                      <span className="font-mono-tech text-xs font-bold text-violet-700 bg-violet-50 border border-violet-100 px-2.5 py-1 rounded-full">{j.step}</span>
                      <div>
                        <div className="font-display font-bold text-[#0A1B5E]">{j.label}</div>
                        <div className="text-sm text-slate-600 mt-0.5">{j.note}</div>
                      </div>
                    </li>
                  ))}
                </ol>
                <Link to="/contact?type=consultation&service=training" className="btn-accent mt-7 text-sm" data-testid="training-student-cta">
                  Join the Student Track <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seven ecosystem pillars */}
      <section data-testid="training-ecosystem-section" className="py-20 bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative container-cd">
          <SectionHeading
            eyebrow="Seven Ecosystem Pillars"
            title="Programs that span the entire training & workforce journey"
            description="Pick one. Layer many. Each pillar is independent in design and unified through one operating ecosystem."
          />

          <div className="mt-16 space-y-6">
            {PILLARS.map((p, i) => (
              <div key={p.id} data-testid={`training-pillar-${i}`} className="card-cd !p-8 relative overflow-hidden group">
                <div className={`absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br ${p.accent} opacity-10 group-hover:opacity-20 transition-opacity blur-3xl`} />
                <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-3">
                    <div className="flex items-start gap-4">
                      <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${p.accent} text-white grid place-items-center shadow-lg shadow-blue-500/10 shrink-0`}>
                        <p.icon size={22} strokeWidth={1.6} />
                      </div>
                      <div>
                        <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-slate-400">{p.tag}</div>
                        <h3 className="font-display text-xl font-bold text-[#0A1B5E] mt-1">{p.title}</h3>
                        <div className="text-sm text-slate-500 mt-1">{p.audience}</div>
                      </div>
                    </div>
                    <p className="mt-6 text-slate-600 leading-relaxed text-[15px]">{p.desc}</p>
                  </div>
                  <div className="lg:col-span-9">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {p.items.map((it, j) => (
                        <div key={it.label} data-testid={`training-pillar-${i}-item-${j}`} className="p-4 rounded-xl border border-slate-200 bg-white hover:border-[#3B82F6] hover:shadow-[0_10px_30px_-15px_rgba(59,130,246,0.35)] transition-all">
                          <div className="flex items-center gap-3">
                            <span className="h-9 w-9 grid place-items-center rounded-lg bg-slate-50 border border-slate-200 text-[#3B82F6]">
                              <it.icon size={16} strokeWidth={1.6} />
                            </span>
                            <div className="font-display font-bold text-[#0A1B5E] text-[15px]">{it.label}</div>
                          </div>
                          <div className="text-xs text-slate-600 mt-2 leading-relaxed">{it.note}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workshop reach footprint */}
      <section data-testid="training-workshop-section" className="py-20 bg-white">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Workshop Network"
            title="On-ground awareness reach"
            description="Awareness workshops run as a network — facilitators, partners and community organisations operating across India."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: GraduationCap, label: "Schools", note: "Online safety · social media hygiene · gaming risks." },
              { icon: BookOpen, label: "Colleges", note: "Career paths · ethical hacking · practical labs." },
              { icon: Users, label: "Communities", note: "Senior citizens · RWAs · women's groups · merchants." },
              { icon: Sparkles, label: "Awareness Campaigns", note: "City and regional digital-safety drives." },
            ].map((w, i) => (
              <div key={w.label} data-testid={`training-workshop-${i}`} className="card-cd">
                <span className="h-12 w-12 grid place-items-center rounded-xl bg-blue-50 border border-blue-100 text-[#3B82F6]">
                  <w.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold text-[#0A1B5E]">{w.label}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{w.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dual CTA */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container-cd grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-cd !p-10 ring-glow-cyan border-cyan-200" data-testid="training-final-corporate">
            <Building2 size={28} className="text-emerald-600" />
            <h3 className="mt-4 font-display text-2xl font-bold text-[#0A1B5E]">Build a corporate training program</h3>
            <p className="mt-3 text-slate-600">For enterprises and institutions — phishing simulation, awareness, leadership and compliance learning.</p>
            <Link to="/contact?type=consultation&service=training" className="btn-accent mt-6" data-testid="training-final-corporate-cta">
              Design a Program <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="card-cd !p-10 ring-glow-purple border-purple-200" data-testid="training-final-student">
            <GraduationCap size={28} className="text-[#6D28D9]" />
            <h3 className="mt-4 font-display text-2xl font-bold text-[#0A1B5E]">Join the student & internship pipeline</h3>
            <p className="mt-3 text-slate-600">For students, interns and early-career professionals — fundamentals through to live SOC exposure.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact?type=consultation&service=training" className="btn-primary" data-testid="training-final-student-cta">
                Apply for Training <ArrowUpRight size={16} />
              </Link>
              <Link to="/careers" className="btn-ghost" data-testid="training-final-careers-cta">
                See Open Roles <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
